import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import type { ReferenceSearchHitPayload } from '@/utils/projectCreateReferenceKnowledge';

export type OrgMemberRosterRow = {
  userId: number;
  realName: string;
  departmentName?: string | null;
  /** 通讯录岗位/职位 */
  position?: string | null;
  /** 飞书/企微等 platform_user_id，供大模型在 JSON 中返回以消除 userId 抄错 */
  platformUserId?: string | null;
};

export type ProjectCreateDraftAiSnapshot = {
  draftKey: string;
  form: {
    name: string;
    owner: string;
    description: string;
    projectDeliveryDate: string;
  };
  aiProjectGoal: string;
  aiProjectType: string;
  aiSuggestedRiskLevel: string;
  phasePlans: unknown[];
};

/** 创建项目页 → AI 同步请求的附加上下文（组织候选人等） */
export const useProjectCreateAiContextStore = defineStore('projectCreateAiContext', () => {
  const orgMemberRoster = shallowRef<OrgMemberRosterRow[]>([]);
  const currentDraftSnapshots = shallowRef<Record<string, ProjectCreateDraftAiSnapshot>>({});
  /** 最近一次创建项目对话预检索命中，用于在模型漏填 url 时在前端补全链接 */
  const lastReferenceSearchHits = shallowRef<ReferenceSearchHitPayload[]>([]);

  function setOrgMemberRoster(rows: OrgMemberRosterRow[]) {
    orgMemberRoster.value = Array.isArray(rows) ? rows : [];
  }

  function setCurrentDraftSnapshot(draftKey: string, snapshot: Omit<ProjectCreateDraftAiSnapshot, 'draftKey'>) {
    const key = String(draftKey || '').trim();
    if (!key) return;
    currentDraftSnapshots.value = {
      ...currentDraftSnapshots.value,
      [key]: { ...snapshot, draftKey: key },
    };
  }

  function clearCurrentDraftSnapshot(draftKey: string) {
    const key = String(draftKey || '').trim();
    if (!key || !currentDraftSnapshots.value[key]) return;
    const next = { ...currentDraftSnapshots.value };
    delete next[key];
    currentDraftSnapshots.value = next;
  }

  function setLastReferenceSearchHits(rows: ReferenceSearchHitPayload[] | null | undefined) {
    lastReferenceSearchHits.value = Array.isArray(rows) ? rows : [];
  }

  /** 供 postAgentChat.context 使用；控制体量避免请求过大 */
  function buildChatContextPayload(draftKey = ''): Record<string, unknown> | undefined {
    const rows = orgMemberRoster.value;
    const capped = rows.slice(0, 400).map((r: OrgMemberRosterRow) => ({
      userId: r.userId,
      realName: (r.realName || '').trim() || `用户${r.userId}`,
      departmentName: (r.departmentName || '').trim(),
      position: (r.position || '').trim(),
      platformUserId: (r.platformUserId || '').trim(),
    }));
    const payload: Record<string, unknown> = {};
    if (capped.length) {
      payload.orgMemberRoster = capped;
      payload.projectCodePatternNotes =
        '默认编号规则：XMYYYYMMDD00001；序号由后端按天原子递增生成，创建页不接受 AI 自行生成或修改。';
    }
    const key = String(draftKey || '').trim();
    const snapshot = key ? currentDraftSnapshots.value[key] : undefined;
    if (snapshot) payload.projectCreateDraftSnapshot = snapshot;
    return Object.keys(payload).length ? payload : undefined;
  }

  return {
    orgMemberRoster,
    setOrgMemberRoster,
    setCurrentDraftSnapshot,
    clearCurrentDraftSnapshot,
    buildChatContextPayload,
    lastReferenceSearchHits,
    setLastReferenceSearchHits,
  };
});
